import React from 'react';
import {
  Paper,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  useTheme,
  alpha,
  Unstable_TrapFocus,
} from '@mui/material';
import { CloseIcon } from '@/components/icons';

import { ChatInput } from './ChatInput';
import { useChatBot, useGetConfig } from './ChatBot';
import { useLauncherEmbedStyle } from '../context/LauncherEmbedStyleContext';
import { useWidgetBrandColors } from './useWidgetBrandColors';
import { scaledFontSize } from './createWidgetTheme';
import { trackWidgetEvent } from '../utils/analytics';
import { getLauncherTeaserMode } from '../utils/getLauncherTeaserMode';
import { WidgetAnnouncerOutlet } from './WidgetAnnouncerProvider';
import { visuallyHiddenSx } from './visuallyHiddenSx';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

interface ChatMinimizedBarProps {
  chatInputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}

export function ChatMinimizedBar({ chatInputRef }: ChatMinimizedBarProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const closeChatLabel = t('drawer.closeChat');
  const { gradient, primaryOnPaper } = useWidgetBrandColors();
  const config = useGetConfig();
  const { mx, zIndex } = useLauncherEmbedStyle();
  const horizontalInset = mx * 2;
  const { history, toggleChatUi, send, isVoiceActive, hasSentMessageInSession } =
    useChatBot();
  const hideForFrequency =
    config.quickStartFrequency === 'until_first_message' &&
    Boolean(hasSentMessageInSession);

  const handleCloseBar = () => {
    trackWidgetEvent('Chat Widget - Chat bar minimized to widget');
    toggleChatUi(false);
  };

  return (
    // EnforceFocus uses document.activeElement, which is the shadow host when
    // focus is inside the widget — disable it so the bar input stays usable.
    <Unstable_TrapFocus open disableRestoreFocus disableEnforceFocus>
      <Box
        sx={{
          zIndex,
          width: 840,
          right: 'calc(50% - 420px)',
          bottom: '-10px',
          height: '140px',
          position: 'fixed',
          maxWidth: `calc(100vw - ${horizontalInset}px)`,
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ position: 'relative' }}>
          <IconButton
            size="small"
            aria-label={closeChatLabel}
            title={closeChatLabel}
            lang={locale}
            color="default"
            onClick={handleCloseBar}
            sx={{
              top: -10,
              zIndex: 1000,
              width: '25px',
              marginRight: 2,
              position: 'absolute',
              bgcolor: alpha(theme.palette.common.white, 0.85),
              '&:hover': { bgcolor: theme.palette.common.white },
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              borderRadius: '100%',
            }}
          >
            <CloseIcon
              fontSize="small"
              sx={{ fontSize: scaledFontSize(theme, 16), color: primaryOnPaper }}
              aria-hidden
            />
          </IconButton>
        </Stack>
        <Paper
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-agent-name-minimized"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            border: '1px solid',
            borderColor: 'grey.300',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(6px)',
            bgcolor: theme.palette.background.default,
            background: `linear-gradient(90deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
          }}
        >
          <WidgetAnnouncerOutlet />
          <Typography id="chat-agent-name-minimized" sx={visuallyHiddenSx}>
            {config.agentName}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              background: `linear-gradient(90deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
              color: gradient.foreground,
            }}
          >
            <Stack direction="column" spacing={2} alignItems="center" sx={{ width: '100%' }}>
              <ChatInput inputRef={chatInputRef} />
              {config.quickStartButtons &&
                config.quickStartButtons.length > 0 &&
                !isVoiceActive &&
                !hideForFrequency &&
                !history.some((msg) => msg.role === 'user') && (
                  <Stack direction="row" sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}>
                    {config.quickStartButtons.map((button, index) => (
                      <Button
                        key={`${button.label}-${index}`}
                        variant="outlined"
                        aria-label={button.label}
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 3,
                          color: primaryOnPaper,
                          p: 1,
                          textTransform: 'none',
                          fontSize: scaledFontSize(theme, 14),
                        }}
                        onClick={() => {
                          trackWidgetEvent('Chat Widget - Quick Start Button Clicked', {
                            buttonLabel: button.label,
                            buttonMessage: button.message,
                            teaserMode: getLauncherTeaserMode(config),
                          });
                          send(button.message, { isQuickStartButton: true });
                        }}
                      >
                        {button.label}
                      </Button>
                    ))}
                  </Stack>
                )}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Unstable_TrapFocus>
  );
}
